import { useEffect } from 'react'
import type { JSX } from 'react'
import {
  Button,
  ChannelIcon,
  Dialog,
  Flex,
  Heading,
  IconButton,
  Notice,
  Text,
  XIcon,
} from '@buffer-mono/popcorn'

import { reloadPage } from '../../../../../../common/utils/urls'
import { BackButton } from '../_shared/components/BackButton/BackButton'
import styles from './WhatsAppPreConnection.module.css'
import { useWhatsAppSignup } from './useWhatsAppSignup'

export type WhatsAppCloseMode = 'close' | 'blocked' | 'reload'

export function WhatsAppPreConnection({
  organizationId,
  channelId,
  onCloseModeChange,
}: {
  organizationId: string
  channelId?: string
  onCloseModeChange?: (mode: WhatsAppCloseMode) => void
}): JSX.Element {
  const { state, prepare, start } = useWhatsAppSignup(organizationId, channelId)
  const connecting =
    state.status === 'connecting' || state.status === 'connected'
  const busy =
    state.status === 'loading' || state.status === 'pending' || connecting
  const requiresReload = state.status === 'error' && state.requiresReload
  const needsRepair = state.status === 'needsRepair'
  const saved = state.status === 'saved'
  const syncing = state.status === 'syncing'
  const channelSaved = needsRepair || saved || syncing
  const connectingMessage =
    state.status === 'connecting' &&
    state.finishType === 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING'
      ? 'Buffer is syncing your WhatsApp Business app. Keep the app open on your phone.'
      : 'Connecting your WhatsApp number…'

  // Closing does not stop the server, so the dialog stays until it answers.
  // A saved Channel needs a page reload to appear in the host's Channel list.
  const closeMode: WhatsAppCloseMode = connecting
    ? 'blocked'
    : channelSaved
      ? 'reload'
      : 'close'
  useEffect(() => {
    onCloseModeChange?.(closeMode)
  }, [closeMode, onCloseModeChange])

  function retry(): void {
    if (requiresReload) window.location.reload()
    else void prepare()
  }

  const retries = state.status === 'error' || needsRepair
  const buttonLabel = requiresReload
    ? 'Refresh page'
    : retries
      ? 'Try again'
      : 'Connect WhatsApp'

  return (
    <>
      <Dialog.Header>
        {closeMode !== 'blocked' && (
          <Flex
            justify={channelSaved ? 'end' : 'between'}
            align="center"
            fullWidth
          >
            {!channelSaved && <BackButton />}
            <Dialog.Close>
              <IconButton label="Close" variant="tertiary" size="small">
                <XIcon />
              </IconButton>
            </Dialog.Close>
          </Flex>
        )}
      </Dialog.Header>
      <Dialog.Body className={styles.body}>
        <Flex direction="column" gap="lg" align="center">
          <ChannelIcon type="whatsapp" color="branded" size="large" />
          <Heading size="lg">Connect WhatsApp</Heading>
          <Text>Manage your WhatsApp direct messages in Buffer.</Text>
          <Flex direction="column" gap="sm">
            <Text weight="bold">Before you connect</Text>
            <Text>You need access to your Meta account.</Text>
            <Text>
              You need control of the phone number you want to connect.
            </Text>
          </Flex>
          {connecting && (
            <output>
              <Text>{connectingMessage}</Text>
            </output>
          )}
          {state.status === 'error' && (
            <Notice variant="error">
              <Notice.Heading>
                We couldn’t complete WhatsApp signup.
              </Notice.Heading>
              <Notice.Text>{state.message}</Notice.Text>
              {requiresReload && (
                <Notice.Text>
                  Close any Meta signup window before refreshing this page.
                </Notice.Text>
              )}
            </Notice>
          )}
          {syncing && (
            <Notice variant="info">
              <Notice.Heading>WhatsApp is connected.</Notice.Heading>
              <Notice.Text>
                Keep the WhatsApp Business app open for a few minutes while it
                syncs.
              </Notice.Text>
            </Notice>
          )}
          {needsRepair && (
            <Notice variant="warning">
              <Notice.Heading>WhatsApp setup isn’t complete.</Notice.Heading>
              <Notice.Text>
                Buffer saved your WhatsApp number but couldn’t finish setting it
                up. Try again with the same number.
              </Notice.Text>
            </Notice>
          )}
          {saved && (
            <Notice variant="warning">
              <Notice.Heading>
                Buffer saved your WhatsApp number.
              </Notice.Heading>
              <Notice.Text>
                Check your Channel list to see if it needs anything else.
              </Notice.Text>
            </Notice>
          )}
        </Flex>
      </Dialog.Body>
      <Dialog.Footer>
        {syncing && (
          <Button size="large" variant="primary" onClick={reloadPage}>
            Refresh page
          </Button>
        )}
        {saved && (
          <Dialog.Close>
            <Button size="large" variant="primary">
              Close
            </Button>
          </Dialog.Close>
        )}
        {!syncing && !saved && (
          <Button
            size="large"
            variant="primary"
            disabled={busy}
            loading={busy}
            onClick={retries ? retry : start}
          >
            {buttonLabel}
          </Button>
        )}
      </Dialog.Footer>
    </>
  )
}
